import { useEffect, useState } from "react";
import { Badge, Button, Dropdown, Empty, List, Space, Tag, Typography } from "antd";
import { CheckSquareOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { fetchPendingApprovals, type PendingApproval } from "../helpers/approvals";
import { fmtMVR } from "../helpers/format";
import { docTarget } from "../helpers/docRoutes";
import { hasAnyGrant } from "../helpers/permissions";
import { useUser } from "../context/UserContext";

/** How many rows the dropdown previews before deferring to the inbox. */
const PREVIEW_LIMIT = 8;

/**
 * Pending approvals are no longer pushed as notifications (they were noise the moment a
 * document sat in a queue) — this bell is the live count instead, read straight from
 * /approvals/pending, which the server has already filtered to what the caller may act on.
 */
export function ApprovalsBell() {
  const [items, setItems] = useState<PendingApproval[]>([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useUser();

  async function load() {
    setItems(await fetchPendingApprovals());
  }

  useEffect(() => {
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Acting on a document elsewhere changes this count, so re-read whenever it is opened.
  function onOpenChange(next: boolean) {
    setOpen(next);
    if (next) load();
  }

  function go(path: string) {
    setOpen(false);
    navigate(path);
  }

  return (
    <Dropdown
      open={open}
      onOpenChange={onOpenChange}
      trigger={["click"]}
      popupRender={() => (
        <div style={{ background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", borderRadius: 8, width: 340, maxHeight: 420, overflow: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px" }}>
            <Typography.Text strong>Pending approvals</Typography.Text>
            <Button type="link" size="small" onClick={() => go("/approvals")}>
              See all
            </Button>
          </div>
          <List
            dataSource={items.slice(0, PREVIEW_LIMIT)}
            locale={{ emptyText: <Empty description="Nothing awaiting your approval" /> }}
            renderItem={(item) => {
              // Unreadable referents stay plain text rather than linking into a 403.
              const target = docTarget(item.instance.documentType, item.instance.documentId);
              const path = target && hasAnyGrant(user, target.permission) ? target.path : null;
              return (
                <List.Item
                  onClick={path ? () => go(path) : undefined}
                  style={{ padding: "8px 12px", cursor: path ? "pointer" : undefined }}
                >
                  <div>
                    <div>{item.docNo}</div>
                    <Space size={4}>
                      <Tag style={{ marginInlineEnd: 0 }}>{item.instance.documentType.replaceAll("_", " ")}</Tag>
                      {item.amountLaari != null && (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {fmtMVR(item.amountLaari)}
                        </Typography.Text>
                      )}
                    </Space>
                  </div>
                </List.Item>
              );
            }}
          />
        </div>
      )}
    >
      <Badge count={items.length} size="small" data-tour="approvals-bell">
        <Button type="text" icon={<CheckSquareOutlined style={{ fontSize: 18 }} />} aria-label="Pending approvals" />
      </Badge>
    </Dropdown>
  );
}
